import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Minus, Plus, Trash2 } from 'lucide-react';
import { Product } from './FightCardProduct';
interface CartItem extends Product {
  quantity: number;
}
interface CartSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartItem[];
  onUpdateQuantity: (id: string, delta: number) => void;
  onRemoveItem: (id: string) => void;
}
export function CartSidebar({
  isOpen,
  onClose,
  cartItems,
  onUpdateQuantity,
  onRemoveItem
}: CartSidebarProps) {
  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  return <AnimatePresence data-id="element-1">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 z-50" style={{
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(4px)'
      }} data-id="element-2" />

          {/* Sidebar */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 28,
        stiffness: 220
      }} className="fixed top-0 right-0 h-full w-full md:w-[440px] z-50 flex flex-col" style={{
        background: 'rgba(12,12,12,0.85)',
        backdropFilter: 'blur(24px)',
        borderLeft: '1px solid rgba(255,255,255,0.07)'
      }} data-id="element-3">
            {/* Header */}
            <div className="p-6 flex justify-between items-center" style={{
          borderBottom: '1px solid rgba(255,255,255,0.06)'
        }} data-id="element-4">
              <h2 className="text-3xl font-bebas tracking-wide" style={{
            color: '#e0e0e0'
          }} data-id="element-5">
                YOUR FIGHT GEAR
              </h2>
              <button onClick={onClose} style={{
            color: '#555'
          }} className="hover:text-white transition-colors" data-id="element-6">
                <X className="w-6 h-6" data-id="element-7" />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4" data-id="element-8">
              {cartItems.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center opacity-50" data-id="element-9">
                  <span className="text-5xl mb-4" data-id="element-10">🥊</span>
                  <h3 className="text-2xl font-bebas mb-2" style={{
              color: '#e0e0e0'
            }} data-id="element-11">
                    YOUR CAGE IS EMPTY
                  </h3>
                  <p className="font-barlow" style={{
              color: '#555'
            }} data-id="element-12">
                    Time to gear up for the next fight.
                  </p>
                  <button onClick={onClose} className="mt-6 font-bebas text-xl underline underline-offset-4 yellow-glow-text" data-id="element-13">
                    START SHOPPING
                  </button>
                </div> : cartItems.map(item => <motion.div layout key={item.id} className="flex gap-4 p-4" style={{
            background: 'rgba(255,255,255,0.03)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.06)'
          }} data-id="element-14">
                    <div className="w-20 h-24 flex-shrink-0 overflow-hidden" data-id="element-15">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover opacity-80" data-id="element-16" />
                    </div>
                    <div className="flex-1 flex flex-col justify-between" data-id="element-17">
                      <div data-id="element-18">
                        <div className="flex justify-between items-start" data-id="element-19">
                          <h4 className="text-lg font-bebas leading-tight mb-1" style={{
                    color: '#e0e0e0'
                  }} data-id="element-20">
                            {item.name}
                          </h4>
                          <button onClick={() => onRemoveItem(item.id)} className="transition-colors" style={{
                    color: '#555'
                  }} onMouseEnter={e => e.currentTarget.style.color = 'rgba(153,27,27,0.8)'} onMouseLeave={e => e.currentTarget.style.color = '#555'} data-id="element-21">
                            <Trash2 className="w-4 h-4" data-id="element-22" />
                          </button>
                        </div>
                        <p className="text-sm font-barlow" style={{
                  color: '#555'
                }} data-id="element-23">
                          {item.category}
                        </p>
                      </div>

                      <div className="flex justify-between items-end" data-id="element-24">
                        <div className="flex items-center gap-3 px-2 py-1" style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.07)'
                }} data-id="element-25">
                          <button onClick={() => onUpdateQuantity(item.id, -1)} style={{
                    color: '#555'
                  }} className="hover:text-white transition-colors" data-id="element-26">
                            <Minus className="w-3 h-3" data-id="element-27" />
                          </button>
                          <span className="text-sm font-barlow font-bold w-4 text-center" style={{
                    color: '#e0e0e0'
                  }} data-id="element-28">
                            {item.quantity}
                          </span>
                          <button onClick={() => onUpdateQuantity(item.id, 1)} style={{
                    color: '#555'
                  }} className="hover:text-white transition-colors" data-id="element-29">
                            <Plus className="w-3 h-3" data-id="element-30" />
                          </button>
                        </div>
                        <span className="text-lg font-barlow font-bold" style={{
                  color: '#e0e0e0'
                }} data-id="element-31">
                          ${item.price * item.quantity}
                        </span>
                      </div>
                    </div>
                  </motion.div>)}
            </div>

            {/* Footer */}
            {cartItems.length > 0 && <div className="p-6" style={{
          borderTop: '1px solid rgba(255,255,255,0.06)'
        }} data-id="element-32">
                <div className="flex justify-between items-center mb-4" data-id="element-33">
                  <span className="font-barlow uppercase tracking-wider" style={{
              color: '#555'
            }} data-id="element-34">
                    Subtotal
                  </span>
                  <span className="text-2xl font-bebas" style={{
              color: '#e0e0e0'
            }} data-id="element-35">
                    ${subtotal}
                  </span>
                </div>
                <p className="text-xs font-barlow mb-4 text-center" style={{
            color: '#555'
          }} data-id="element-36">
                  Shipping & taxes calculated at checkout
                </p>
                <button className="w-full py-4 font-bebas text-xl tracking-wider transition-all duration-300" style={{
            background: 'rgba(153,27,27,0.8)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(153,27,27,0.35)',
            color: '#e0e0e0',
            boxShadow: '0 0 20px rgba(153,27,27,0.15)'
          }} onMouseEnter={e => {
            ;
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(127,29,29,0.9)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 30px rgba(153,27,27,0.25)';
          }} onMouseLeave={e => {
            ;
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(153,27,27,0.8)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 20px rgba(153,27,27,0.15)';
          }} data-id="element-37">
                  CHECKOUT
                </button>
              </div>}
          </motion.div>
        </>}
    </AnimatePresence>;
}